import React, { useEffect, useState } from 'react';
import { fetchTeamMembers, shareSavedFile } from '../services/api';
import { Button, ErrorText, Loading, errorMessage } from './ui';

// Emailing a saved report or answer to people on the project.
//
// Recipients are PICKED FROM THE ROSTER, never typed. Two reasons, and the
// second is the one that decides it:
//
//   - A typed address is a typo away from sending a compliance report to a
//     stranger, and there is no recall.
//   - The API only accepts addresses belonging to active members of the
//     workspace (see savedShare.js). A free-text field would therefore offer
//     something the server refuses, which reads as a broken feature rather
//     than as the restriction it is.
//
// Invited-but-not-accepted members are shown as unavailable rather than
// hidden: "why isn't Dave in the list" is a question worth answering on the
// screen, because the answer is that Dave has not accepted his seat yet.
export default function ShareSavedFile({ blobName, title, onDone }) {
  const [members, setMembers] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    fetchTeamMembers()
      .then((data) => { if (live) setMembers(Array.isArray(data?.members) ? data.members : []); })
      .catch((e) => { if (live) { setError(errorMessage(e)); setMembers([]); } });
    return () => { live = false; };
  }, []);

  function toggle(email) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  }

  async function send() {
    setBusy(true);
    setError('');
    setResult('');
    try {
      const res = await shareSavedFile({ blobName, to: [...picked], note });
      const sent = Number(res?.sent) || 0;
      // "Accepted by the provider" is not "delivered", and saying "sent" for a
      // pending send is the kind of small lie that costs trust the first time
      // somebody does not receive one.
      setResult(
        `${res?.pending ? 'Queued for' : 'Sent to'} ${sent} ${sent === 1 ? 'person' : 'people'}.`
        + (res?.rejected?.length ? ` ${res.rejected.length} address(es) were not on the team and were skipped.` : '')
      );
      setPicked(new Set());
      setNote('');
      onDone?.(res);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const available = (members || []).filter((m) => m.status === 'active' && m.email);
  const pending = (members || []).filter((m) => m.status === 'invited' && m.email);

  return (
    <div className="saved-answer-body">
      <ErrorText>{error}</ErrorText>
      {result ? <p className="saved">{result}</p> : null}

      {members === null ? <Loading text="Loading the team…" /> : null}

      {members !== null && !available.length ? (
        <p className="card-hint" style={{ marginTop: 0 }}>
          Nobody else has an active seat on this workspace yet, so there is no one to share with.
        </p>
      ) : null}

      {available.map((m) => (
        <label key={m.email} className="procore-option" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={picked.has(m.email)} onChange={() => toggle(m.email)} />
          <span className="procore-option-name">{m.name || m.email}</span>
          <span className="procore-option-code">
            {m.name ? m.email : ''}
            {m.org ? ` · ${m.org}` : ''}
          </span>
        </label>
      ))}

      {pending.length ? (
        <p className="card-hint">
          {pending.length === 1 ? '1 person has' : `${pending.length} people have`} been invited but
          not accepted yet, so {pending.length === 1 ? 'they cannot' : 'they cannot'} be sent project
          documents.
        </p>
      ) : null}

      {available.length ? (
        <>
          <textarea
            className="input"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="A note to go with it (optional)"
            style={{ marginTop: 8 }}
          />
          <div className="row" style={{ gap: 8, marginTop: 8 }}>
            <Button compact disabled={busy || !picked.size} onClick={send}>
              {busy ? 'Sending…' : `Email${picked.size ? ` ${picked.size}` : ''}`}
            </Button>
            <span className="card-hint">{title ? `Attaches "${title}".` : ''}</span>
          </div>
        </>
      ) : null}
    </div>
  );
}
